// src/features/system/devices/hooks/useTelemetryStreamers.ts
// ---------------------------------------------------------------------------
// Hook for managing which IoT devices participate in telemetry streaming.
//
// Opt-out model: all devices are enabled by default.  The user can disable
// specific devices via the StreamerManagerDialog (PACDaemon CLI-style
// indexed multi-select).  Selection persists to localStorage per tenant.
//
// The hook gates which device IDs reach useTelemetryStream, cutting
// unnecessary mux-bin scope_keys and orchestrator /hot-stream/history
// queries for devices the user doesn't care about.
//
// NOTE: uses Record<string, true> instead of Set because tsconfig
// targets ES5 without downlevelIteration.
// ---------------------------------------------------------------------------
import { useCallback, useMemo, useState, useEffect } from 'react';
import type { Device } from '../api/iotDevicesApi';

/** Selection map: keys are device_id, values are always true. */
type SelectionMap = Record<string, true>;

// -- Selection map helpers ------------------------------------------------

function mapFrom(ids: string[]): SelectionMap {
  const m: SelectionMap = {};
  for (const id of ids) m[id] = true;
  return m;
}

function mapHas(m: SelectionMap, id: string): boolean {
  return m[id] === true;
}

function mapKeys(m: SelectionMap): string[] {
  return Object.keys(m);
}

function mapSize(m: SelectionMap): number {
  return Object.keys(m).length;
}

function mapClone(m: SelectionMap): SelectionMap {
  return { ...m };
}

// -- localStorage persistence --------------------------------------------
const STORAGE_PREFIX = 'pac:telemetry:streamers:';

function storageKey(tenantId: number | undefined): string {
  return `${STORAGE_PREFIX}${tenantId ?? 'default'}`;
}

function loadSelection(tenantId: number | undefined): SelectionMap | null {
  try {
    const raw = localStorage.getItem(storageKey(tenantId));
    if (!raw) return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? mapFrom(arr) : null;
  } catch { return null; }
}

function persistSelection(tenantId: number | undefined, sel: SelectionMap): void {
  try {
    localStorage.setItem(storageKey(tenantId), JSON.stringify(mapKeys(sel)));
  } catch { /* quota exceeded or private mode -- silent */ }
}

// -- PACDaemon-style index parser -----------------------------------------
// Supports: "1 3 5 6", "1,3,5,6", "1-3 5", "1-5" (mixed)

export function parseIndexes(input: string, maxIndex: number): number[] | string {
  const normalised = input.replace(/,/g, ' ').trim();
  if (!normalised) return 'No indexes provided.';

  const seen: Record<number, true> = {};
  const result: number[] = [];
  const tokens = normalised.split(/\s+/);

  for (const tok of tokens) {
    const dashPos = tok.indexOf('-', 1);
    if (dashPos !== -1 && dashPos < tok.length - 1) {
      const lo = parseInt(tok.slice(0, dashPos), 10);
      const hi = parseInt(tok.slice(dashPos + 1), 10);
      if (Number.isNaN(lo) || Number.isNaN(hi)) return `Invalid token: ${tok}`;
      const [min, max] = lo <= hi ? [lo, hi] : [hi, lo];
      for (let i = min; i <= max; i++) {
        if (i < 1 || i > maxIndex) return `Index out of range: ${i}`;
        if (!seen[i]) { seen[i] = true; result.push(i); }
      }
    } else {
      const idx = parseInt(tok, 10);
      if (Number.isNaN(idx)) return `Invalid token: ${tok}`;
      if (idx < 1 || idx > maxIndex) return `Index out of range: ${idx}`;
      if (!seen[idx]) { seen[idx] = true; result.push(idx); }
    }
  }

  return result;
}

// -- Hook -----------------------------------------------------------------

export interface TelemetryStreamersResult {
  /** Device IDs enabled for streaming (subset of allDevices). */
  enabledIds: string[];
  /** All IoT devices passed in. */
  allDevices: Device[];
  /** Check if a specific device is enabled. */
  isEnabled: (deviceId: string) => boolean;
  /** Toggle a single device. */
  toggle: (deviceId: string) => void;
  /** Enable all devices. */
  enableAll: () => void;
  /** Disable all devices. */
  disableAll: () => void;
  /** Set exact selection from an array of device IDs. */
  setEnabled: (ids: string[]) => void;
  /** Apply PACDaemon-style index selection (e.g. "1 3 5"). */
  applyIndexSelection: (input: string) => string | null;
  /** Count: enabled / total. */
  enabledCount: number;
  totalCount: number;
}

export function useTelemetryStreamers(
  allDevices: Device[],
  tenantId: number | undefined,
): TelemetryStreamersResult {
  const [selection, setSelection] = useState<SelectionMap>(() => {
    const saved = loadSelection(tenantId);
    return saved ?? mapFrom(allDevices.map(d => d.device_id));
  });

  // When tenant changes, reload from localStorage.
  useEffect(() => {
    const saved = loadSelection(tenantId);
    setSelection(saved ?? mapFrom(allDevices.map(d => d.device_id)));
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // When allDevices changes (new device discovered), add any unknown IDs
  // to the selection (opt-out: new devices are enabled by default).
  useEffect(() => {
    // Read saved preference once per effect invocation — avoids N redundant
    // localStorage reads inside the state updater function.
    const saved = loadSelection(tenantId);
    setSelection(prev => {
      let changed = false;
      const next = mapClone(prev);
      for (const dev of allDevices) {
        if (!mapHas(next, dev.device_id)) {
          if (!saved || mapSize(saved) === 0 || mapHas(saved, dev.device_id)) {
            next[dev.device_id] = true;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [allDevices.map(d => d.device_id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist on every selection change
  useEffect(() => {
    persistSelection(tenantId, selection);
  }, [selection, tenantId]);

  const isEnabled = useCallback(
    (deviceId: string) => mapHas(selection, deviceId),
    [selection],
  );

  const toggle = useCallback((deviceId: string) => {
    setSelection(prev => {
      const next = mapClone(prev);
      if (mapHas(next, deviceId)) delete next[deviceId];
      else next[deviceId] = true;
      return next;
    });
  }, []);

  const enableAll = useCallback(() => {
    setSelection(mapFrom(allDevices.map(d => d.device_id)));
  }, [allDevices]);

  const disableAll = useCallback(() => {
    setSelection({});
  }, []);

  const setEnabled = useCallback((ids: string[]) => {
    setSelection(mapFrom(ids));
  }, []);

  const applyIndexSelection = useCallback((input: string): string | null => {
    const result = parseIndexes(input, allDevices.length);
    if (typeof result === 'string') return result;
    const ids: string[] = [];
    for (const idx of result) {
      ids.push(allDevices[idx - 1].device_id);
    }
    setSelection(mapFrom(ids));
    return null;
  }, [allDevices]);

  const enabledIds = useMemo(
    () => allDevices
      .filter(d => mapHas(selection, d.device_id))
      .map(d => d.device_id)
      .sort(),  // Stable lexicographic order — prevents effect re-fire from backend order jitter
    [allDevices, selection],
  );

  return {
    enabledIds,
    allDevices,
    isEnabled,
    toggle,
    enableAll,
    disableAll,
    setEnabled,
    applyIndexSelection,
    enabledCount: enabledIds.length,
    totalCount: allDevices.length,
  };
}
